/**
 * ForgeAI Govern™ - Admin → Integrations page
 *
 * Destination cards with a combobox-style event picker:
 *   - Closed state shows "{N} events selected" + a compact chip preview
 *   - Opens a popover with Presets, Grouped options, and a selected-count footer
 *   - Click outside / Esc closes
 *
 * Webhook secrets are entered by the admin, encrypted at rest in D1 via
 * AES-GCM (PHI_ENCRYPTION_KEY), and never echoed back.
 */

const ALL_COMPLY_EVENTS = [
  'ai_use_case', 'risk_assessment', 'incident', 'poam_item',
  'governance_exception', 'monitoring_event', 'vendor_change_notification',
  'ai_asset', 'impact_assessment', 'model_version', 'finding', 'evidence', 'policy',
];
const ALERT_EVENTS = [
  'incident', 'monitoring_event', 'governance_exception', 'finding',
  'poam_item', 'risk_assessment', 'ai_use_case', 'vendor_change_notification',
];

const EVENT_LABELS = {
  ai_use_case: 'AI use case',
  risk_assessment: 'Risk assessment',
  incident: 'Incident',
  poam_item: 'POA&M item',
  governance_exception: 'Exception',
  monitoring_event: 'Monitoring event',
  vendor_change_notification: 'Vendor change',
  ai_asset: 'AI asset',
  impact_assessment: 'Impact assessment',
  model_version: 'Model version',
  finding: 'Finding',
  evidence: 'Evidence',
  policy: 'Policy',
};

const EVENT_DESCRIPTIONS = {
  ai_use_case: 'Intake decisions (approve, conditional, reject)',
  risk_assessment: '6-dim risk approvals and residual-risk memos',
  incident: 'AI incidents — create, update, close',
  poam_item: 'Remediation tracking (Plan of Actions & Milestones)',
  governance_exception: 'Risk acceptances with expiry',
  monitoring_event: 'Drift, prompt abuse, data leakage, unauthorized changes',
  vendor_change_notification: 'Third-party AI vendor updates / outages',
  ai_asset: 'AI system registry mutations',
  impact_assessment: 'Algorithmic impact / fairness evaluations',
  model_version: 'Model version lifecycle transitions',
  finding: 'Audit / assessment / external findings',
  evidence: 'Evidence artifacts + legal-hold transitions',
  policy: 'Governance policy approvals / retirements',
};

const COMPLY_EVENT_GROUPS = [
  { label: 'Governance & approvals', events: ['ai_use_case', 'risk_assessment', 'impact_assessment', 'policy', 'model_version', 'ai_asset'] },
  { label: 'Operations',              events: ['incident', 'poam_item', 'monitoring_event', 'finding'] },
  { label: 'Vendor & evidence',       events: ['vendor_change_notification', 'evidence'] },
  { label: 'Exceptions',              events: ['governance_exception'] },
];
const ALERT_EVENT_GROUPS = [
  { label: 'Critical alerts', events: ['incident', 'monitoring_event'] },
  { label: 'Governance',      events: ['ai_use_case', 'risk_assessment', 'governance_exception', 'finding'] },
  { label: 'Other',           events: ['poam_item', 'vendor_change_notification'] },
];

const COMPLY_PRESETS = [
  { label: 'Minimal',         desc: 'Risks + incidents + remediation only', events: ['risk_assessment', 'incident', 'poam_item'] },
  { label: 'Recommended',     desc: 'Default GRC recordkeeping scope',       events: ['ai_use_case', 'risk_assessment', 'incident', 'poam_item', 'governance_exception', 'monitoring_event'] },
  { label: 'Governance only', desc: 'Approvals and policy lifecycle',        events: ['ai_use_case', 'risk_assessment', 'governance_exception', 'policy', 'impact_assessment'] },
  { label: 'Everything',      desc: 'Full event fan-out',                    events: ALL_COMPLY_EVENTS },
];
const ALERT_PRESETS = [
  { label: 'Critical only', desc: 'Incidents + monitoring events',           events: ['incident', 'monitoring_event'] },
  { label: 'Recommended',   desc: 'Pageable governance alerts',              events: ['incident', 'monitoring_event', 'governance_exception', 'finding'] },
  { label: 'Everything',    desc: 'Full alert stream',                       events: ALERT_EVENTS },
];

function fmtDate(s) { return s ? new Date(s).toLocaleString() : '—'; }

function healthDot(lastError) {
  if (lastError) return '<span class="health-dot health-dot-error" title="Last delivery failed"></span>';
  return '<span class="health-dot health-dot-ok" title="Last delivery OK"></span>';
}

/**
 * Render a combobox-style event picker.
 * - Stores state in hidden <input type="checkbox"> elements so the form
 *   submit handler keeps working unchanged.
 * - Exposes a button + popover for selection.
 */
function eventPicker({ pickerId, inputName, allEvents, groups, presets, selected }) {
  const subs = new Set(selected || []);
  const hiddenInputs = allEvents.map((e) => `
    <input type="checkbox" name="${inputName}" value="${e}" ${subs.has(e) ? 'checked' : ''} hidden>
  `).join('');

  const presetsHtml = presets.map((p, i) => `
    <button type="button" class="event-preset" data-picker="${pickerId}" data-preset-index="${i}">
      <span class="event-preset-label">${p.label}</span>
      <span class="event-preset-desc">${p.desc}</span>
    </button>`).join('');

  const groupsHtml = groups.map((g) => `
    <div class="event-group">
      <div class="event-group-head">
        <span class="event-group-label">${g.label}</span>
        <button type="button" class="event-group-select" data-picker="${pickerId}" data-group-events="${g.events.join(',')}">Select all</button>
      </div>
      <div class="event-group-options">
        ${g.events.map((e) => `
          <label class="event-option">
            <input type="checkbox" data-picker="${pickerId}" data-event="${e}" ${subs.has(e) ? 'checked' : ''}>
            <span class="event-option-check"></span>
            <span class="event-option-label">${EVENT_LABELS[e] || e}</span>
            <span class="event-option-desc">${EVENT_DESCRIPTIONS[e] || ''}</span>
          </label>`).join('')}
      </div>
    </div>`).join('');

  // Preview chips (read-only, shows what's currently selected)
  const selectedChips = [...subs].slice(0, 8).map((e) => `<span class="selected-chip">${EVENT_LABELS[e] || e}</span>`).join('');
  const overflow = subs.size > 8 ? `<span class="selected-chip selected-chip-more">+${subs.size - 8} more</span>` : '';

  return `
    <div class="event-picker" data-picker-root="${pickerId}">
      ${hiddenInputs}

      <button type="button" class="event-picker-trigger" data-picker-trigger="${pickerId}" aria-haspopup="listbox" aria-expanded="false">
        <div class="event-picker-trigger-text">
          <span class="event-picker-count" data-picker-count="${pickerId}">${subs.size}</span>
          <span>of ${allEvents.length} event types selected</span>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      <div class="event-picker-preview" data-picker-preview="${pickerId}">
        ${subs.size === 0 ? '<span class="text-muted" style="font-size:12px">No events selected — this destination will not receive any traffic.</span>' : selectedChips + overflow}
      </div>

      <div class="event-picker-popover" data-picker-popover="${pickerId}" role="listbox" aria-label="Event types">
        <div class="event-picker-section">
          <div class="event-picker-section-title">Quick presets</div>
          <div class="event-preset-grid">${presetsHtml}</div>
        </div>
        <div class="event-picker-section">
          <div class="event-picker-section-title">All events by category</div>
          ${groupsHtml}
        </div>
        <div class="event-picker-footer">
          <span class="event-picker-status">
            <strong data-picker-count-footer="${pickerId}">${subs.size}</strong> selected
          </span>
          <div>
            <button type="button" class="btn btn-sm btn-ghost" data-picker-clear="${pickerId}">Clear all</button>
            <button type="button" class="btn btn-sm btn-primary" data-picker-done="${pickerId}">Done</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

export const integrations = {
  async page() {
    let comply = {}, tenantSettings = {};
    try { comply = (await API.getForgeComplyConfig()).data || {}; } catch { /* non-admin or not configured */ }
    try { tenantSettings = (await API.getTenantSettings()).data || {}; } catch { /* */ }

    const complyEnabled = !!comply.enabled;
    const alertEnabled = !!tenantSettings.alert_webhook_enabled;

    return `
      <div class="page-header">
        <div>
          <h2>Integrations</h2>
          <p>Outbound connectors for GRC recordkeeping and alerting. Secrets are encrypted at rest and never displayed.</p>
        </div>
      </div>

      <!-- ===== Cana Ridge Assure ===== -->
      <section class="integration-card" id="comply-card">
        <header class="integration-hero">
          <div class="integration-identity">
            <div class="integration-icon integration-icon-primary" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
            </div>
            <div class="integration-title">
              <h3>Cana Ridge Assure Connector</h3>
              <p class="integration-subtitle">Authoritative GRC recordkeeping and reporting — every governance decision signed and forwarded to Cana Ridge Assure.</p>
              <div class="integration-meta">
                <span class="meta-pill">Signed payloads</span>
                <span class="meta-pill">Idempotent delivery</span>
                <span class="meta-pill">1m → 24h retry</span>
              </div>
            </div>
          </div>
          <div class="integration-controls">
            <span class="status-pill ${complyEnabled ? 'status-pill-ok' : 'status-pill-off'}">
              <span class="status-pill-dot"></span>${complyEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <label class="toggle-switch" title="Enable connector">
              <input type="checkbox" id="comply-enabled" ${complyEnabled ? 'checked' : ''}>
              <span class="toggle-track"><span class="toggle-thumb"></span></span>
            </label>
          </div>
        </header>

        <form id="comply-form" class="integration-body">
          <div class="field-section">
            <div class="field-section-head">
              <h4>Destination</h4>
              <p>Where signed event payloads are POSTed.</p>
            </div>
            <div class="form-group">
              <label for="comply-url">Webhook URL</label>
              <input type="url" id="comply-url" placeholder="https://comply.example.com/api/forgeai/events" value="${comply.webhook_url || ''}">
              <small>HTTPS required. Share this URL with the Cana Ridge Assure team when coordinating.</small>
            </div>
          </div>

          <div class="field-section">
            <div class="field-section-head">
              <h4>Signing secret</h4>
              <p>${comply.secret_configured ? `Currently set: <code class="secret-hint">${comply.secret_masked || 'via env'}</code>${comply.secret_updated_at ? `, updated ${fmtDate(comply.secret_updated_at)}` : ''}` : 'Not configured yet.'}</p>
            </div>
            <div class="form-group secret-input">
              <label for="comply-secret">Secret</label>
              <div class="input-with-button">
                <input type="password" id="comply-secret" placeholder="Paste a 32+ byte random secret (blank = keep existing)" autocomplete="new-password">
                <button type="button" class="input-button" data-toggle-secret="comply-secret" aria-label="Show or hide secret">Show</button>
              </div>
              <small>Generate with <code>openssl rand -base64 32</code>. Must be shared with the Cana Ridge Assure team.</small>
            </div>
          </div>

          <div class="field-section">
            <div class="field-section-head">
              <h4>Subscribed events</h4>
              <p>Only checked event types are sent to this destination. Use a preset or pick individually.</p>
            </div>
            <div class="form-group">
              ${eventPicker({
                pickerId: 'comply',
                inputName: 'comply-events',
                allEvents: ALL_COMPLY_EVENTS,
                groups: COMPLY_EVENT_GROUPS,
                presets: COMPLY_PRESETS,
                selected: comply.subscribed_events,
              })}
            </div>
          </div>

          <footer class="integration-footer">
            <div class="integration-health">
              ${healthDot(comply.last_error)}
              <div>
                <div class="health-line"><strong>Last delivery:</strong> ${fmtDate(comply.last_delivery_at)}</div>
                ${comply.last_error ? `<div class="health-line health-line-error"><strong>Error:</strong> ${comply.last_error}</div>` : ''}
              </div>
            </div>
            <div class="integration-actions">
              <button type="button" class="btn btn-outline" id="comply-test">Send test event</button>
              <button type="submit" class="btn btn-primary">Save changes</button>
            </div>
          </footer>
        </form>
      </section>

      <!-- ===== Alert webhook ===== -->
      <section class="integration-card" id="alert-card">
        <header class="integration-hero">
          <div class="integration-identity">
            <div class="integration-icon integration-icon-warning" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            </div>
            <div class="integration-title">
              <h3>Alert &amp; Pager Webhook</h3>
              <p class="integration-subtitle">Real-time stream for PagerDuty, Opsgenie, Slack, or SIEM. Independent failure domain from Cana Ridge Assure.</p>
              <div class="integration-meta">
                <span class="meta-pill">Signed payloads</span>
                <span class="meta-pill">Critical events</span>
                <span class="meta-pill">Fire-and-forget</span>
              </div>
            </div>
          </div>
          <div class="integration-controls">
            <span class="status-pill ${alertEnabled ? 'status-pill-ok' : 'status-pill-off'}">
              <span class="status-pill-dot"></span>${alertEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <label class="toggle-switch" title="Enable alert webhook">
              <input type="checkbox" id="alert-enabled" ${alertEnabled ? 'checked' : ''}>
              <span class="toggle-track"><span class="toggle-thumb"></span></span>
            </label>
          </div>
        </header>

        <form id="alert-form" class="integration-body">
          <div class="field-section">
            <div class="field-section-head">
              <h4>Destination</h4>
              <p>PagerDuty Events API, Opsgenie webhook, Slack inbound, or a SIEM ingest URL.</p>
            </div>
            <div class="form-group">
              <label for="alert-url">Webhook URL</label>
              <input type="url" id="alert-url" placeholder="https://events.pagerduty.com/integration/.../enqueue" value="${tenantSettings.alert_webhook_url || ''}">
            </div>
          </div>

          <div class="field-section">
            <div class="field-section-head">
              <h4>Signing secret</h4>
              <p>${tenantSettings.alert_webhook_secret_configured ? `Currently set: <code class="secret-hint">${tenantSettings.alert_webhook_secret_masked || 'via env'}</code>` : 'Not configured yet.'}</p>
            </div>
            <div class="form-group secret-input">
              <label for="alert-secret">Secret</label>
              <div class="input-with-button">
                <input type="password" id="alert-secret" placeholder="Paste a 32+ byte random secret (blank = keep existing)" autocomplete="new-password">
                <button type="button" class="input-button" data-toggle-secret="alert-secret" aria-label="Show or hide secret">Show</button>
              </div>
            </div>
          </div>

          <div class="field-section">
            <div class="field-section-head">
              <h4>Subscribed events</h4>
              <p>Alert destinations typically subscribe to a subset — incidents + monitoring events is the common shape.</p>
            </div>
            <div class="form-group">
              ${eventPicker({
                pickerId: 'alert',
                inputName: 'alert-events',
                allEvents: ALERT_EVENTS,
                groups: ALERT_EVENT_GROUPS,
                presets: ALERT_PRESETS,
                selected: tenantSettings.alert_webhook_subscribed_events,
              })}
            </div>
          </div>

          <footer class="integration-footer">
            <div class="integration-health">
              ${healthDot(tenantSettings.alert_webhook_last_error)}
              <div>
                <div class="health-line"><strong>Last delivery:</strong> ${fmtDate(tenantSettings.alert_webhook_last_delivery_at)}</div>
                ${tenantSettings.alert_webhook_last_error ? `<div class="health-line health-line-error"><strong>Error:</strong> ${tenantSettings.alert_webhook_last_error}</div>` : ''}
              </div>
            </div>
            <div class="integration-actions">
              <button type="submit" class="btn btn-primary">Save changes</button>
            </div>
          </footer>
        </form>
      </section>

      <!-- ===== Event ledger ===== -->
      <section class="card integration-ledger">
        <div class="card-header">
          <h3>Recent outbound events</h3>
          <span class="text-muted" style="font-size:12px">Last 25 attempts across all destinations</span>
        </div>
        <div class="card-body">
          <div id="integ-events-list"><em class="text-muted">Loading…</em></div>
        </div>
      </section>
    `;
  },

  async init() {
    // ---- Secret show/hide toggles ----
    document.querySelectorAll('[data-toggle-secret]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-toggle-secret');
        const input = document.getElementById(id);
        if (!input) return;
        if (input.type === 'password') { input.type = 'text'; btn.textContent = 'Hide'; }
        else { input.type = 'password'; btn.textContent = 'Show'; }
      });
    });

    // ---- Event picker: open/close/presets/groups/selected-chips ----
    const pickerConfigs = {
      comply: { all: ALL_COMPLY_EVENTS, presets: COMPLY_PRESETS, inputName: 'comply-events' },
      alert:  { all: ALERT_EVENTS,      presets: ALERT_PRESETS,  inputName: 'alert-events' },
    };
    const openPopover = (pickerId) => {
      const pop = document.querySelector(`[data-picker-popover="${pickerId}"]`);
      const trig = document.querySelector(`[data-picker-trigger="${pickerId}"]`);
      if (!pop || !trig) return;
      pop.classList.add('open');
      trig.setAttribute('aria-expanded', 'true');
    };
    const closePopover = (pickerId) => {
      const pop = document.querySelector(`[data-picker-popover="${pickerId}"]`);
      const trig = document.querySelector(`[data-picker-trigger="${pickerId}"]`);
      if (!pop || !trig) return;
      pop.classList.remove('open');
      trig.setAttribute('aria-expanded', 'false');
    };
    const syncPickerState = (pickerId) => {
      const cfg = pickerConfigs[pickerId];
      if (!cfg) return;
      // Sync hidden form inputs from popover checkboxes
      const visibleChecks = document.querySelectorAll(`input[data-picker="${pickerId}"][data-event]`);
      const selectedSet = new Set();
      visibleChecks.forEach(cb => { if (cb.checked) selectedSet.add(cb.getAttribute('data-event')); });
      document.querySelectorAll(`input[name="${cfg.inputName}"]`).forEach(hidden => {
        hidden.checked = selectedSet.has(hidden.value);
      });
      // Update count
      const cnt = selectedSet.size;
      const countEl = document.querySelector(`[data-picker-count="${pickerId}"]`);
      if (countEl) countEl.textContent = cnt;
      const footCountEl = document.querySelector(`[data-picker-count-footer="${pickerId}"]`);
      if (footCountEl) footCountEl.textContent = cnt;
      // Update preview chips
      const prev = document.querySelector(`[data-picker-preview="${pickerId}"]`);
      if (prev) {
        const arr = [...selectedSet];
        if (arr.length === 0) {
          prev.innerHTML = '<span class="text-muted" style="font-size:12px">No events selected — this destination will not receive any traffic.</span>';
        } else {
          const head = arr.slice(0, 8).map(e => `<span class="selected-chip">${EVENT_LABELS[e] || e}</span>`).join('');
          const tail = arr.length > 8 ? `<span class="selected-chip selected-chip-more">+${arr.length - 8} more</span>` : '';
          prev.innerHTML = head + tail;
        }
      }
    };

    // Trigger buttons
    document.querySelectorAll('[data-picker-trigger]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = btn.getAttribute('data-picker-trigger');
        const pop = document.querySelector(`[data-picker-popover="${id}"]`);
        if (pop?.classList.contains('open')) closePopover(id);
        else {
          // close any other open popover first
          Object.keys(pickerConfigs).forEach(k => closePopover(k));
          openPopover(id);
        }
      });
    });

    // Preset buttons
    document.querySelectorAll('[data-preset-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        const pickerId = btn.getAttribute('data-picker');
        const idx = parseInt(btn.getAttribute('data-preset-index'), 10);
        const preset = pickerConfigs[pickerId].presets[idx];
        if (!preset) return;
        const wanted = new Set(preset.events);
        document.querySelectorAll(`input[data-picker="${pickerId}"][data-event]`).forEach(cb => {
          cb.checked = wanted.has(cb.getAttribute('data-event'));
        });
        syncPickerState(pickerId);
      });
    });

    // Group "Select all" buttons
    document.querySelectorAll('[data-group-events]').forEach(btn => {
      btn.addEventListener('click', () => {
        const pickerId = btn.getAttribute('data-picker');
        const events = btn.getAttribute('data-group-events').split(',');
        // Toggle: if all in group are already selected, deselect; else select all
        const checks = events.map(e => document.querySelector(`input[data-picker="${pickerId}"][data-event="${e}"]`)).filter(Boolean);
        const allChecked = checks.every(cb => cb.checked);
        checks.forEach(cb => cb.checked = !allChecked);
        btn.textContent = allChecked ? 'Select all' : 'Unselect all';
        syncPickerState(pickerId);
      });
    });

    // Individual checkboxes inside popover
    document.querySelectorAll('input[data-picker][data-event]').forEach(cb => {
      cb.addEventListener('change', () => {
        const pickerId = cb.getAttribute('data-picker');
        syncPickerState(pickerId);
      });
    });

    // Clear + Done
    document.querySelectorAll('[data-picker-clear]').forEach(btn => {
      btn.addEventListener('click', () => {
        const pickerId = btn.getAttribute('data-picker-clear');
        document.querySelectorAll(`input[data-picker="${pickerId}"][data-event]`).forEach(cb => cb.checked = false);
        syncPickerState(pickerId);
      });
    });
    document.querySelectorAll('[data-picker-done]').forEach(btn => {
      btn.addEventListener('click', () => {
        closePopover(btn.getAttribute('data-picker-done'));
      });
    });

    // Click outside / Escape closes all popovers
    document.addEventListener('click', (e) => {
      Object.keys(pickerConfigs).forEach(id => {
        const root = document.querySelector(`[data-picker-root="${id}"]`);
        if (root && !root.contains(e.target)) closePopover(id);
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') Object.keys(pickerConfigs).forEach(id => closePopover(id));
    });

    // ---- Comply form submit ----
    const complyForm = document.getElementById('comply-form');
    if (complyForm) {
      complyForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const body = {
          enabled: document.getElementById('comply-enabled').checked,
          webhook_url: document.getElementById('comply-url').value.trim() || null,
          subscribed_events: [...document.querySelectorAll('input[name="comply-events"]:checked')].map(i => i.value),
        };
        const secret = document.getElementById('comply-secret').value;
        if (secret.length > 0) body.webhook_secret = secret;
        try {
          await API.updateForgeComplyConfig(body);
          App.toast('Cana Ridge Assure connector saved', 'success');
          App.navigate('integrations');
        } catch (err) {
          App.toast(`Failed to save: ${err?.message || 'error'}`, 'error');
        }
      });
    }

    // ---- Alert form submit ----
    const alertForm = document.getElementById('alert-form');
    if (alertForm) {
      alertForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const body = {
          alert_webhook_enabled: document.getElementById('alert-enabled').checked,
          alert_webhook_url: document.getElementById('alert-url').value.trim() || null,
          alert_webhook_subscribed_events: [...document.querySelectorAll('input[name="alert-events"]:checked')].map(i => i.value),
        };
        const secret = document.getElementById('alert-secret').value;
        if (secret.length > 0) body.alert_webhook_secret = secret;
        try {
          await API.updateTenantSettings(body);
          App.toast('Alert webhook saved', 'success');
          App.navigate('integrations');
        } catch (err) {
          App.toast(`Failed to save: ${err?.message || 'error'}`, 'error');
        }
      });
    }

    // ---- Recent events ledger ----
    const listEl = document.getElementById('integ-events-list');
    if (listEl) {
      try {
        const events = (await API.listForgeComplyEvents({ limit: '25' })).data || [];
        if (!events.length) {
          listEl.innerHTML = '<div class="empty-state" style="padding:32px 16px"><em class="text-muted">No outbound events yet. Save configuration above and trigger any governance action to populate this list.</em></div>';
        } else {
          listEl.innerHTML = `
            <div class="table-container">
              <table>
                <thead><tr>
                  <th>Occurred</th><th>Destination</th><th>Entity</th><th>Operation</th>
                  <th>Status</th><th class="text-right">Attempts</th><th>Last error</th><th></th>
                </tr></thead>
                <tbody>
                  ${events.map(e => `
                    <tr>
                      <td>${fmtDate(e.occurred_at)}</td>
                      <td><span class="dest-pill dest-${e.destination}">${e.destination}</span></td>
                      <td><code class="inline-code">${e.entity_type}</code></td>
                      <td>${e.operation}</td>
                      <td><span class="badge badge-${e.status === 'delivered' ? 'approved' : e.status === 'failed' ? 'moderate' : e.status === 'dead_letter' ? 'critical' : 'draft'}">${App.formatLabel(e.status)}</span></td>
                      <td class="text-right">${e.attempts || 0}</td>
                      <td><small class="text-muted">${e.last_error || '—'}</small></td>
                      <td class="text-right">
                        ${e.status !== 'delivered' ? `<button class="btn btn-sm btn-outline" data-redeliver="${e.id}">Retry</button>` : ''}
                      </td>
                    </tr>`).join('')}
                </tbody>
              </table>
            </div>`;
          listEl.querySelectorAll('[data-redeliver]').forEach(btn => btn.addEventListener('click', async (ev) => {
            const id = ev.target.getAttribute('data-redeliver');
            try {
              await API.redeliverForgeComplyEvent(id);
              App.toast('Redelivery attempted', 'success');
              App.navigate('integrations');
            } catch (err) { App.toast(`Redelivery failed: ${err?.message}`, 'error'); }
          }));
        }
      } catch {
        listEl.innerHTML = '<em class="text-muted">Unable to load events (admin access required).</em>';
      }
    }
  },
};
